// src/components/login/admin/admin-brand-keys-page.tsx
//
// Admin-only: configure a brand's own LLM API keys. Each provider is set
// independently to either the platform's shared global key or the brand's own
// custom key. The brand-level mode ('custom' if any provider is custom, else
// 'global') is derived from those per-provider choices and sent on save.
//
// Security: keys are write-only from the UI's perspective — the backend never
// returns them, only {configured, last4}. A newly-entered key is validated with
// its provider before it can be saved; a wrong key is never persisted.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  brandApiKeysApi,
  LLM_PROVIDERS,
  PROVIDER_LABELS,
  type BrandLlmKeys,
  type LlmProvider,
} from "@/api/brandApiKeys";
import { brandsApi, type AssignableBrand } from "@/api/brands";
import { BrandAimodeBackupSection } from "./brand-aimode-backup-section";
import { Button } from "../../ui/button";
import {
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Loader2,
  RefreshCcw,
  Save,
  Search,
} from "lucide-react";

type ProviderChoice = "global" | "custom";

function isAdminFromStorage() {
  return (
    localStorage.getItem("is_admin") === "true" ||
    sessionStorage.getItem("is_admin") === "true"
  );
}

/** Turn a raw provider/SDK error (often a 401 JSON blob) into one friendly line. */
function friendlyKeyError(detail: string): string {
  const d = (detail || "").toLowerCase();
  if (
    d.includes("401") ||
    d.includes("incorrect api key") ||
    d.includes("invalid_request_error") ||
    d.includes("invalid x-api-key") ||
    d.includes("api key not valid") ||
    d.includes("api_key_invalid") ||
    d.includes("authentication") ||
    d.includes("unauthor")
  ) {
    return "This API key isn't valid. Double-check it and paste it again.";
  }
  if (
    d.includes("429") ||
    d.includes("quota") ||
    d.includes("rate limit") ||
    d.includes("insufficient_quota")
  ) {
    return "Key recognised, but it's rate-limited or out of quota.";
  }
  if (d.includes("403") || d.includes("permission") || d.includes("forbidden")) {
    return "This key doesn't have access to this provider.";
  }
  if (
    d.includes("timeout") ||
    d.includes("network") ||
    d.includes("connection") ||
    d.includes("could not") ||
    d.includes("failed to fetch")
  ) {
    return "Couldn't reach the provider to validate this key. Try again.";
  }
  return "Couldn't validate this key. Please re-check it and try again.";
}

const emptyChoices = (): Record<LlmProvider, ProviderChoice> =>
  LLM_PROVIDERS.reduce(
    (acc, p) => ({ ...acc, [p]: "global" as ProviderChoice }),
    {} as Record<LlmProvider, ProviderChoice>,
  );

export function AdminBrandKeysPage() {
  const [brands, setBrands] = useState<AssignableBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);

  const [config, setConfig] = useState<BrandLlmKeys | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Per-provider: global vs custom, plus any newly-typed key.
  const [choices, setChoices] = useState<Record<LlmProvider, ProviderChoice>>(emptyChoices);
  const [drafts, setDrafts] = useState<Partial<Record<LlmProvider, string>>>({});

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<LlmProvider | null>(null);
  // Which endpoint to test the Perplexity key against (#90): Sonar chat
  // completions (retiring 2026-09-27) or the Agent API. Every other provider
  // has only one call surface, so this only ever applies to "perplexity".
  const [perplexityTestEndpoint, setPerplexityTestEndpoint] = useState<"sonar" | "agent">("sonar");
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  // Per-provider inline validation message, shown under that provider's input.
  const [fieldMsg, setFieldMsg] = useState<
    Partial<Record<LlmProvider, { ok: boolean; text: string }>>
  >({});

  const clearFieldMsg = (provider: LlmProvider) =>
    setFieldMsg((m) => {
      if (!m[provider]) return m;
      const next = { ...m };
      delete next[provider];
      return next;
    });

  // ── Load brands ────────────────────────────────────────────────────
  const loadBrands = useCallback(async () => {
    setErr("");
    setLoadingBrands(true);
    try {
      if (!isAdminFromStorage()) {
        setErr("Admin access required.");
        setBrands([]);
        return;
      }
      setBrands(await brandsApi.assignable());
    } catch (e: any) {
      setErr(e?.message || "Failed to load brands");
    } finally {
      setLoadingBrands(false);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // ── Load a brand's key config ──────────────────────────────────────
  const applyConfig = (cfg: BrandLlmKeys) => {
    setConfig(cfg);
    setFieldMsg({});
    // A provider is "custom" when (and only when) it has a stored key.
    setChoices(
      LLM_PROVIDERS.reduce(
        (acc, p) => ({
          ...acc,
          [p]: cfg.providers[p]?.configured ? "custom" : "global",
        }),
        {} as Record<LlmProvider, ProviderChoice>,
      ),
    );
  };

  const loadConfig = useCallback(async (brand: string) => {
    setErr("");
    setSuccess("");
    setLoadingConfig(true);
    setDrafts({});
    try {
      applyConfig(await brandApiKeysApi.get(brand));
    } catch (e: any) {
      setErr(e?.message || "Failed to load brand keys");
      setConfig(null);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const selectBrand = (brand: string) => {
    setSelectedBrand(brand);
    loadConfig(brand);
  };

  const filteredBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.brand_name.toLowerCase().includes(q));
  }, [brands, search]);

  // ── Save ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedBrand) return;
    setErr("");
    setSuccess("");
    setSaving(true);
    try {
      const keys: Partial<Record<LlmProvider, string>> = {}; // new keys to store
      const toDelete: LlmProvider[] = []; // custom→global on a configured provider
      const incomplete: LlmProvider[] = []; // custom chosen but no key available

      for (const p of LLM_PROVIDERS) {
        const draft = (drafts[p] || "").trim();
        const configured = !!config?.providers[p]?.configured;
        if (choices[p] === "custom") {
          if (draft) keys[p] = draft;
          else if (!configured) incomplete.push(p);
        } else if (configured) {
          // Switched a configured provider back to global → remove its key.
          toDelete.push(p);
        }
      }

      if (incomplete.length) {
        setFieldMsg((m) => {
          const next = { ...m };
          incomplete.forEach((p) => {
            next[p] = { ok: false, text: "Enter a key, or switch back to Global." };
          });
          return next;
        });
        setErr("Some providers need attention — see the messages below.");
        return;
      }

      // Validate every newly-entered key with its provider BEFORE persisting.
      // A wrong key must never be saved — block the whole save if any fail.
      const entered = Object.entries(keys) as [LlmProvider, string][];
      if (entered.length) {
        const checks = await Promise.all(
          entered.map(async ([provider, key]) => {
            try {
              const res = await brandApiKeysApi.test(selectedBrand, provider, key);
              return { provider, ok: res.ok, detail: res.detail };
            } catch (e: any) {
              return { provider, ok: false, detail: e?.message || "Validation failed" };
            }
          }),
        );
        const failures = checks.filter((c) => !c.ok);
        if (failures.length) {
          setFieldMsg((m) => {
            const next = { ...m };
            failures.forEach((f) => {
              next[f.provider] = { ok: false, text: friendlyKeyError(f.detail) };
            });
            return next;
          });
          setErr("Not saved — fix the highlighted keys below.");
          return;
        }
      }

      // Apply removals first, then upsert new keys + the derived brand mode.
      for (const p of toDelete) {
        await brandApiKeysApi.remove(selectedBrand, p);
      }

      const anyCustom = LLM_PROVIDERS.some(
        (p) =>
          choices[p] === "custom" &&
          (!!config?.providers[p]?.configured || !!(drafts[p] || "").trim()),
      );

      const updated = await brandApiKeysApi.update(selectedBrand, {
        mode: anyCustom ? "custom" : "global",
        keys: Object.keys(keys).length ? keys : undefined,
      });
      applyConfig(updated);
      setDrafts({});
      setSuccess("Changes saved.");
    } catch (e: any) {
      setErr(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (provider: LlmProvider) => {
    if (!selectedBrand) return;
    setErr("");
    setSuccess("");
    clearFieldMsg(provider);
    setTesting(provider);
    try {
      const draft = (drafts[provider] || "").trim();
      const endpoint = provider === "perplexity" ? perplexityTestEndpoint : undefined;
      const res = await brandApiKeysApi.test(selectedBrand, provider, draft || undefined, endpoint);
      const endpointLabel = res.endpoint === "agent" ? "Agent API" : res.endpoint === "sonar" ? "Sonar" : null;
      setFieldMsg((m) => ({
        ...m,
        [provider]: res.ok
          ? { ok: true, text: endpointLabel ? `Key is valid (${endpointLabel}).` : "Key is valid." }
          : { ok: false, text: friendlyKeyError(res.detail) },
      }));
    } catch (e: any) {
      setFieldMsg((m) => ({
        ...m,
        [provider]: { ok: false, text: friendlyKeyError(e?.message || "") },
      }));
    } finally {
      setTesting(null);
    }
  };

  // ── Shared inline styles (theme tokens) ────────────────────────────
  const cardStyle: React.CSSProperties = {
    backgroundColor: "var(--card)",
    border: "1px solid var(--border)",
    borderRadius: 12,
  };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 9,
    border: "1px solid var(--border)",
    backgroundColor: "var(--input-background)",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    color: "var(--foreground)",
  };

  const segBtn = (active: boolean): React.CSSProperties => ({
    padding: "5px 12px",
    fontSize: 12,
    cursor: "pointer",
    border: "none",
    borderRadius: 7,
    backgroundColor: active ? "var(--alpha-teal-10)" : "transparent",
    color: active ? "var(--alpha-teal)" : "var(--muted-foreground)",
    fontWeight: active ? 600 : 400,
  });

  return (
    <div className="px-4 md:px-6 py-5 md:py-6">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <KeyRound style={{ width: 22, height: 22, color: "var(--alpha-teal)" }} />
            <h2
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "clamp(var(--text-xl), 4.5vw, var(--text-2xl))",
                fontWeight: 700,
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              Brand API Keys
            </h2>
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: "var(--muted-foreground)",
            }}
          >
            Admin-only. Give a paying brand its own LLM provider keys so its usage
            is billed to the brand's own accounts instead of the platform's
            shared keys.
          </div>
        </div>
        <Button
          onClick={loadBrands}
          disabled={loadingBrands}
          variant="secondary"
          className="w-full sm:w-auto flex-shrink-0"
          style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}
        >
          <RefreshCcw style={{ width: 16, height: 16 }} />
          Refresh
        </Button>
      </div>

      {/* ── Instructions ───────────────────────────────────────────── */}
      <div
        className="mt-4 p-4 md:p-5 rounded-xl"
        style={{
          backgroundColor: "var(--alpha-teal-10)",
          border: "1px solid var(--alpha-teal)",
        }}
      >
        <div
          style={{
            fontSize: "var(--text-sm)",
            fontWeight: 600,
            color: "var(--foreground)",
            marginBottom: 8,
          }}
        >
          How it works
        </div>
        <ol
          style={{
            margin: 0,
            paddingLeft: 18,
            fontSize: 13,
            lineHeight: 1.7,
            color: "var(--foreground)",
          }}
        >
          <li>Pick a brand from the list on the left.</li>
          <li>
            For each provider, choose <strong>Global</strong> to use the
            platform's shared key, or <strong>Brand key</strong> to enter a key
            for this brand only.
          </li>
          <li>
            Use <strong>Test</strong> to verify a key with the provider, then{" "}
            <strong>Save</strong>. A wrong key is rejected and won't be saved.
          </li>
          <li>
            Saved keys are encrypted and never shown again — only the last 4
            characters are displayed.
          </li>
        </ol>
      </div>

      {/* ── Alerts ─────────────────────────────────────────────────── */}
      {err && (
        <div
          className="mt-4 p-3 flex items-start gap-2 rounded-lg"
          style={{
            backgroundColor: "var(--infrared-coral-5)",
            border: "1px solid var(--infrared-coral)",
          }}
          role="alert"
        >
          <AlertCircle
            style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0, color: "var(--infrared-coral)" }}
          />
          <span
            style={{
              color: "var(--infrared-coral)",
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: "pre-line",
            }}
          >
            {err}
          </span>
        </div>
      )}
      {success && (
        <div
          className="mt-4 p-3 flex items-center gap-2 rounded-lg"
          style={{
            backgroundColor: "var(--signal-green-5, var(--card))",
            border: "1px solid var(--signal-green)",
          }}
          role="status"
        >
          <CheckCircle2 style={{ width: 16, height: 16, flexShrink: 0, color: "var(--signal-green)" }} />
          <span style={{ color: "var(--signal-green)", fontSize: 13 }}>{success}</span>
        </div>
      )}

      {/* ── Body: brand list + config panel ───────────────────────── */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 md:gap-6">
        {/* Brand picker */}
        <div className="p-3 md:p-4" style={cardStyle}>
          <div className="relative">
            <Search
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                width: 15,
                height: 15,
                color: "var(--muted-foreground)",
                pointerEvents: "none",
              }}
            />
            <input
              type="search"
              name="brand-key-search"
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search brands…"
              style={{ ...inputStyle, paddingLeft: 36 }}
            />
          </div>

          {loadingBrands ? (
            <div
              className="mt-3 p-4 flex items-center justify-center gap-2"
              style={{ color: "var(--muted-foreground)" }}
            >
              <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} />
              <span style={{ fontSize: 13 }}>Loading…</span>
            </div>
          ) : (
            <div className="mt-3 space-y-1 overflow-auto" style={{ maxHeight: 520 }}>
              {filteredBrands.map((b) => {
                const active = selectedBrand === b.brand_name;
                return (
                  <button
                    key={b.brand_name}
                    onClick={() => selectBrand(b.brand_name)}
                    className="w-full truncate text-left"
                    style={{
                      padding: "9px 12px",
                      borderRadius: 9,
                      border: active ? "1px solid var(--alpha-teal)" : "1px solid transparent",
                      backgroundColor: active ? "var(--alpha-teal-10)" : "transparent",
                      color: active ? "var(--alpha-teal)" : "var(--foreground)",
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      cursor: "pointer",
                    }}
                  >
                    {b.brand_name}
                  </button>
                );
              })}
              {filteredBrands.length === 0 && (
                <div className="p-3" style={{ fontSize: 13, color: "var(--muted-foreground)" }}>
                  No brands found.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Config panel */}
        <div className="p-4 md:p-5" style={cardStyle}>
          {!selectedBrand ? (
            <div
              className="flex flex-col items-center justify-center text-center"
              style={{ minHeight: 240, color: "var(--muted-foreground)" }}
            >
              <KeyRound style={{ width: 34, height: 34, opacity: 0.4, marginBottom: 10 }} />
              <div style={{ fontSize: 13 }}>Select a brand to configure its API keys.</div>
            </div>
          ) : loadingConfig ? (
            <div
              className="flex items-center justify-center gap-2"
              style={{ minHeight: 240, color: "var(--muted-foreground)" }}
            >
              <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
              <span style={{ fontSize: 13 }}>Loading config…</span>
            </div>
          ) : (
            <>
              <div style={{ fontSize: "var(--text-base)", fontWeight: 700, color: "var(--foreground)" }}>
                {selectedBrand}
              </div>
              <p style={{ marginTop: 4, marginBottom: 18, fontSize: 12, color: "var(--muted-foreground)" }}>
                Set each provider to the shared global key or this brand's own key.
              </p>

              {/* Per-provider rows */}
              <div className="space-y-5">
                {LLM_PROVIDERS.map((provider) => {
                  const status = config?.providers[provider];
                  const configured = !!status?.configured;
                  const choice = choices[provider];
                  const isCustom = choice === "custom";
                  return (
                    <div
                      key={provider}
                      style={{
                        paddingBottom: 16,
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      <div className="flex items-center justify-between gap-3" style={{ marginBottom: 10 }}>
                        <label style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)" }}>
                          {PROVIDER_LABELS[provider]}
                        </label>
                        {/* Per-provider Global / Brand-key segmented control */}
                        <div
                          style={{
                            display: "inline-flex",
                            padding: 3,
                            borderRadius: 9,
                            border: "1px solid var(--border)",
                            backgroundColor: "var(--input-background)",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setChoices((c) => ({ ...c, [provider]: "global" }));
                              clearFieldMsg(provider);
                            }}
                            style={segBtn(!isCustom)}
                          >
                            Global
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setChoices((c) => ({ ...c, [provider]: "custom" }));
                              clearFieldMsg(provider);
                            }}
                            style={segBtn(isCustom)}
                          >
                            Brand key
                          </button>
                        </div>
                      </div>

                      {isCustom ? (
                        <>
                          <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                            <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                              {configured
                                ? "Leave blank to keep the current key."
                                : "Enter this brand's key for this provider."}
                            </span>
                            {configured && (
                              <span style={{ fontSize: 12, color: "var(--signal-green)", fontWeight: 500 }}>
                                Configured ••••{status?.last4}
                              </span>
                            )}
                          </div>
                          {provider === "perplexity" && (
                            <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                              <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                                Test against:
                              </span>
                              <div
                                style={{
                                  display: "inline-flex",
                                  padding: 3,
                                  borderRadius: 9,
                                  border: "1px solid var(--border)",
                                  backgroundColor: "var(--input-background)",
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPerplexityTestEndpoint("sonar");
                                    clearFieldMsg(provider);
                                  }}
                                  style={segBtn(perplexityTestEndpoint === "sonar")}
                                >
                                  Sonar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPerplexityTestEndpoint("agent");
                                    clearFieldMsg(provider);
                                  }}
                                  style={segBtn(perplexityTestEndpoint === "agent")}
                                >
                                  Agent
                                </button>
                              </div>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              type="password"
                              name={`brand-key-${provider}`}
                              autoComplete="new-password"
                              data-lpignore="true"
                              data-1p-ignore
                              placeholder={configured ? "Enter a new key to replace" : "Paste API key"}
                              value={drafts[provider] ?? ""}
                              onChange={(e) => {
                                setDrafts((d) => ({ ...d, [provider]: e.target.value }));
                                clearFieldMsg(provider);
                              }}
                              style={inputStyle}
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              disabled={testing === provider}
                              onClick={() => handleTest(provider)}
                              style={{ flexShrink: 0 }}
                            >
                              {testing === provider ? (
                                <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} />
                              ) : (
                                "Test"
                              )}
                            </Button>
                          </div>
                          {fieldMsg[provider] && (
                            <div
                              className="flex items-center gap-1.5"
                              style={{
                                marginTop: 6,
                                fontSize: 12,
                                color: fieldMsg[provider]!.ok
                                  ? "var(--signal-green)"
                                  : "var(--infrared-coral)",
                              }}
                            >
                              {fieldMsg[provider]!.ok ? (
                                <CheckCircle2 style={{ width: 13, height: 13, flexShrink: 0 }} />
                              ) : (
                                <AlertCircle style={{ width: 13, height: 13, flexShrink: 0 }} />
                              )}
                              <span>{fieldMsg[provider]!.text}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <span style={{ fontSize: 13, color: "var(--muted-foreground)" }}>
                          Using the platform's shared global key.
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end" style={{ marginTop: 22 }}>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  style={{ display: "flex", gap: 8, alignItems: "center" }}
                >
                  {saving ? (
                    <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} />
                  ) : (
                    <Save style={{ width: 16, height: 16 }} />
                  )}
                  Save
                </Button>
              </div>

              <BrandAimodeBackupSection
                brand={selectedBrand}
                inputStyle={inputStyle}
                segBtn={segBtn}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminBrandKeysPage;
